
import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Customized } from 'recharts';
import { dashboardApi } from '../services/api';
import { FeeComparisonResponse } from '../types';
import { useAuth } from '../auth/AuthContext';
import { IconButton, Select, MenuItem, FormControl } from '@mui/material';



const ZebraBackground = ({ offset }: any) => {
  const x = offset?.left ?? 0;
  const y = offset?.top ?? 0;
  const w = offset?.width ?? 0;
  const h = offset?.height ?? 0;
  const bands = 5;
  const bandH = h / bands;
  return (
    <g>
      {Array.from({ length: bands }).map((_, i) => (
        <rect key={i} x={x} y={y + i * bandH} width={w} height={bandH}
          fill={i % 2 === 0 ? '#f5f5f5' : '#ffffff'} />
      ))}
    </g>
  );
};

const Dashboard: React.FC = () => {
  // --- 応募数の推移グラフ ---
  const renderApplicationTrendGraph = () => {
    if (!dashboardData) return null;
    // 1〜12月の枠を必ず用意し、APIのmonthly_dataから該当月があれば値を、なければ0をセット
    const monthlyApplicationData = Array.from({ length: 12 }, (_, idx) => {
      const month = idx + 1;
      // item.month: "2026年1月" のような文字列
      const found = dashboardData.monthly_data.find((item) => {
        const match = typeof item.month === 'string' && item.month.match(/^([0-9]{4})年([0-9]{1,2})月$/);
        if (!match) return false;
        const y = Number(match[1]);
        const m = Number(match[2]);
        return y === currentYear && m === month;
      });
      return {
        month,
        count: found ? found.applications : 0,
        monthLabel: `${month}`
      };
    });
    const now = new Date();
    const thisMonthApp = monthlyApplicationData[now.getMonth()]?.count || 0;
    const lastMonthApp = now.getMonth() > 0 ? monthlyApplicationData[now.getMonth() - 1]?.count || 0 : 0;
    const yearTotalApp = monthlyApplicationData.reduce((sum, item) => sum + item.count, 0);

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
          応募数の推移
        </Typography>
        {/* サマリーカード */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: '今月', value: thisMonthApp },
            { label: '先月', value: lastMonthApp },
            { label: `${currentYear}年度`, value: yearTotalApp },
          ].map((item) => (
            <Grid item xs={4} key={item.label}>
              <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.label}
                </Typography>
                <Typography fontWeight="bold" color="text.tertiary" sx={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                  {item.value}<Typography component="span" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold', ml: '2px' }}>名</Typography>
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {/* 棒グラフ */}
        <Box sx={{ width: '100%', height: 220, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyApplicationData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <Customized component={ZebraBackground} />
              <XAxis
                dataKey="monthLabel"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                label={{ value: `${currentYear}年(月)`, position: 'insideBottom', offset: -10, fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={20} />
              <Tooltip
                formatter={(value: number) => [`${value}名`, '応募数']}
                labelFormatter={(label) => `${label}月`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
              />
              <Bar dataKey="count" fill="#1976d2" barSize={10} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  // --- 募集数の推移グラフ ---
  const renderRecruitmentTrendGraph = () => {
    if (!dashboardData) return null;
    const monthlyRecruitmentData = Array.from({ length: 12 }, (_, idx) => {
      const month = idx + 1;
      const found = dashboardData.monthly_data.find((item) => {
        const match = typeof item.month === 'string' && item.month.match(/^([0-9]{4})年([0-9]{1,2})月$/);
        if (!match) return false;
        const y = Number(match[1]);
        const m = Number(match[2]);
        return y === currentYear && m === month;
      });
      return {
        month,
        count: found ? found.recruitments : 0,
        monthLabel: `${month}`
      };
    });
    const now = new Date();
    const thisMonthRec = monthlyRecruitmentData[now.getMonth()]?.count || 0;
    const lastMonthRec = now.getMonth() > 0 ? monthlyRecruitmentData[now.getMonth() - 1]?.count || 0 : 0;
    const yearTotalRec = monthlyRecruitmentData.reduce((sum, item) => sum + item.count, 0);

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
          募集回数の推移
        </Typography>
        {/* サマリーカード */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: '今月', value: thisMonthRec },
            { label: '先月', value: lastMonthRec },
            { label: `${currentYear}年度`, value: yearTotalRec },
          ].map((item) => (
            <Grid item xs={4} key={item.label}>
              <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.label}
                </Typography>
                <Typography fontWeight="bold" color="text.tertiary" sx={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                  {item.value}<Typography component="span" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold', ml: '2px' }}>名</Typography>
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {/* 棒グラフ */}
        <Box sx={{ width: '100%', height: 220, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyRecruitmentData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <Customized component={ZebraBackground} />
              <XAxis
                dataKey="monthLabel"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                label={{ value: `${currentYear}年(月)`, position: 'insideBottom', offset: -10, fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={20} />
              <Tooltip
                formatter={(value: number) => [`${value}名`, '募集回数']}
                labelFormatter={(label) => `${label}月`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
              />
              <Bar dataKey="count" fill="#1976d2" barSize={10} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { storeId } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<FeeComparisonResponse | null>(null);
  const currentYear = new Date().getFullYear();
  const yearOptions = dashboardData?.available_years?.length
    ? dashboardData.available_years
    : [currentYear];
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  useEffect(() => {
    const fetchDashboard = async () => {
      setIsLoading(true);
      try {
        const dashboardRes = await dashboardApi.getFeeComparison(storeId!, selectedYear);
        setDashboardData(dashboardRes);
        setError(null);
      } catch (e) {
        setError('ダッシュボードデータの取得に失敗しました');
      } finally {
        setIsLoading(false);
      }
    };
    if (storeId) fetchDashboard();
  }, [storeId, selectedYear, refreshTick]);
  

  const renderEmployeePromotion = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>社員登用</Typography>
      <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">社員登用データは今後連携予定です</Typography>
      </Box>
    </Box>
  );

  // 手数料比較表示（モバイル・デスクトップ共通）
  const renderFeeComparisonGraph = () => {
    if (!dashboardData) return null;
    const { monthly_data, commission_rate } = dashboardData;
    // 合計値
    const totalOtherService = monthly_data.reduce((sum, item) => sum + item.commission_fee, 0);
    // 課題6: Re:crew累計利用料金は実請求（FIXED_FEE + ②振込手数料）に一致する
    // 月別 fixed_fee の合算を使う（従来の fixed_fee_per_month × 月数 は②を反映しない）。
    const totalRecrew = monthly_data.reduce((sum, item) => sum + item.fixed_fee, 0);
    const totalDifference = totalOtherService - totalRecrew;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
          Re:crewで削減できた金額
        </Typography>

        {/* メイン金額ボックス */}
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, py: 2.5, px: 2, textAlign: 'center', mb: 1.5, bgcolor: '#fff' }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0.5 }}>
            {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(totalDifference)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {/* 店舗作成日取得は省略 */}
            合計お得額
          </Typography>
        </Box>

        {/* 比較ボックス */}
        <Grid container spacing={1.5} alignItems="stretch">
          <Grid item xs={6} sx={{ display: 'flex' }}>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#fff' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(totalRecrew)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block', fontSize: '0.68rem' }}>
                Re:crewの累計利用料金
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sx={{ display: 'flex' }}>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#fff' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(totalOtherService)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block', fontSize: '0.68rem' }}>
                従来のスキマバイト手数料
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
          ※手数料率{Math.round(commission_rate * 100)}%で算出
        </Typography>
      </Box>
    );
  };


  // 共通表示（モバイル・デスクトップで同じ構成）
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ヘッダー */}
      <Box sx={{ pt: 0, pb: { xs: 1, sm: 1.5 }, pl: 0, pr: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => window.history.back()} sx={{ color: '#3c3c3c', p: 0.5, mr: 1.5, flexShrink: 0 }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#3c3c3c' }}>
              <SignalCellularAltIcon sx={{ color: '#898989', fontSize: 22 }} />
              データ
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  disabled={isLoading}
                  sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>{y}年</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setRefreshTick((t) => t + 1)}
                disabled={isLoading}
                size={isMobile ? 'small' : 'medium'}
              >
                更新
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {renderFeeComparisonGraph()}
            {renderEmployeePromotion()}
            {renderRecruitmentTrendGraph()}
            {renderApplicationTrendGraph()}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;